import { useAppSelector, useAppDispatch } from '@/app/stores/store';
import { toggleSidebar } from '@/app/stores/slices/uiSlice';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import { useLogout } from '@/app/modules/auth/api/auth.api';
import { useState, useRef, useEffect } from 'react';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { ThemeSwitcher } from '@/components/ui/theme-switcher';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export function Header() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const language = useAppSelector((state) => state.ui.language);
  const { mutate: logout } = useLogout();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRTL = language === 'ar';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogoutClick = () => {
    logout();
    setIsMenuOpen(false);
  };

  return (
    <header className="relative z-40 h-16 shrink-0 border-b border-border/70 bg-card/90 shadow-sm backdrop-blur">
      <div className={cn(
        "flex h-full items-center justify-between px-6")}>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => dispatch(toggleSidebar())}
            className="lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <ThemeSwitcher />
          {user && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-gray-300 bg-gradient-to-br from-gray-100 to-gray-200 text-sm font-semibold text-gray-700 transition-all hover:scale-[1.02] hover:from-gray-200 hover:to-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:border-gray-600 dark:from-gray-700 dark:to-gray-800 dark:text-gray-200 dark:hover:from-gray-600 dark:hover:to-gray-700"
                aria-label={t('common.userMenu')}
              >
                {user.name ? getInitials(user.name) : 'U'}
              </button>

              {isMenuOpen && (
                <div
                  className={cn(
                    'absolute z-[100] mt-2 w-48 rounded-xl border border-border/70 bg-white/95 p-1 shadow-xl backdrop-blur dark:border-gray-700 dark:bg-gray-800/95',
                    isRTL ? 'left-0' : 'right-0',
                  )}
                >
                  <div className="py-1">
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setIsMenuOpen(false);
                      }}
                      className={cn(
                        "block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors dark:text-gray-200 dark:hover:bg-gray-700",
                        isRTL ? "text-right" : "text-left"
                      )}
                    >
                      {t('navigation.profile')}
                    </button>
                    {user.role === 'ADMIN' && (
                      <button
                        onClick={() => {
                          navigate('/users');
                          setIsMenuOpen(false);
                        }}
                        className={cn(
                          "block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors dark:text-gray-200 dark:hover:bg-gray-700",
                          isRTL ? "text-right" : "text-left"
                        )}
                      >
                        {t('navigation.users')}
                      </button>
                    )}
                    <button
                      onClick={handleLogoutClick}
                      className={cn(
                        "block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors dark:text-gray-200 dark:hover:bg-gray-700",
                        isRTL ? "text-right" : "text-left"
                      )}
                    >
                      {t('navigation.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

